import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import TableComponent from "../TableComponent";
import Pagination from "../../view/common/pagination/Pagination";
import RowDetailsModal from "../RowDetailsModal"; 
import { updateFilters, resetFilters } from "../../redux/slices/filterSlice";
import { RootState } from "../../redux/slices/store";
import { getData } from "../../api/ApiServices";
import "../../styles/StockExpiry.css";

const tableRowStyles = `
  .table-component tbody tr {
    cursor: pointer;
  }
  .table-component tbody tr:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

const StockAged: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const filters = useSelector((state: RootState) => state.filters);

  const [agedStocksData, setAgedStocksData] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNoRecords, setHasNoRecords] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [sortBy, setSortBy] = useState<string>("agedDays");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [lastFilterState, setLastFilterState] = useState<any>({});
  const excludeFieldsForRacks = ["createdBy","tagid", "rackid","Parts"];
  

  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const columns = [
    {
      key: "parts",
      label: "Part No",
      nameKey: "internalPartNo",
      render: (value: any) =>
        typeof value === "object" ? value?.internalPartNo : "N/A",
      sortable: true,
    },
    {
      key: "parts",
      label: "Part Name",
      nameKey: "partName",
      render: (value: any) =>
        typeof value === "object" ? value?.partName : "N/A",
      sortable: true,
    },
    {
      key: "category",
      label: "Category",
      nameKey: "name",
      render: (value: any) =>
        typeof value === "object" ? value?.name : "N/A",
      sortable: true,
    },
    {
      key: "uoms",
      label: "UOM",
      nameKey: "name",
      render: (value: any) =>
        typeof value === "object" ? value?.name : "N/A",
      sortable: true,
    },
    { 
      key: "qty", 
      label: "Quantity", 
      sortable: true,
      isRightAlign: true,
    },
    { 
      key: "agedDays", 
      label: "Aged Days", 
      sortable: true,
      isRightAlign: true,
      render: (value: number) => (
        <span className={value > 0 ? "ageing-status" : "warning-status"}>
          {value}
        </span>
      )
    },
  ];

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };


  const handleRowClick = (row: any) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRow(null);
  };

  useEffect(() => {
    const hasFilterChanged = JSON.stringify(filters) !== JSON.stringify(lastFilterState);
    
    if (hasFilterChanged) {
      setIsFiltering(Object.keys(filters).length > 0);
      setLastFilterState(filters);
      
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchAgedStocks();
      }
    }
  }, [filters]);

  useEffect(() => {
    fetchAgedStocks();
  }, [currentPage, itemsPerPage, sortBy, sortOrder]);

  useEffect(() => {
    const addTableRowClickListeners = () => {
      const tableRows = document.querySelectorAll('.table-component tbody tr');
      
      tableRows.forEach((row, index) => {
        row.addEventListener('click', () => {
          if (agedStocksData[index]) {
            handleRowClick(agedStocksData[index]);
          }
        });
      });
    };
    
    if (!isLoading && agedStocksData.length > 0) {
      setTimeout(addTableRowClickListeners, 100);
    }
    return () => {
      const tableRows = document.querySelectorAll('.table-component tbody tr');
      tableRows.forEach(row => {
        row.replaceWith(row.cloneNode(true)); 
      });
    };
  }, [agedStocksData, isLoading]);

  const fetchAgedStocks = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response: any = await getData("/stocks/aged", {
        page: currentPage,
        limit: itemsPerPage,
        filter: filters,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });
      
      console.log("API Response for Aged Stocks:", response);
      
      if (response?.data?.data) {
        setAgedStocksData(response.data.data);
        
        if (currentPage === 1 && !isFiltering && response.data.data.length > 0) {
          setOriginalData(response.data.data);
        }
        
        setTotalItems(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
        setHasNoRecords(response.data.data.length === 0);
      } else {
        setAgedStocksData([]);
        setHasNoRecords(true);
      }
    } catch (err) {
      console.error("Error fetching aged stock data:", err);
      setError("Failed to load aged stocks data. Please try again later.");
      setAgedStocksData([]);
      setTotalItems(0);
      setTotalPages(0);
      setHasNoRecords(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = useCallback((filters: Record<string, any>) => {
    Object.entries(filters).forEach(([key, value]) => {
      dispatch(updateFilters({ key, value: String(value) }));
    });
  }, [dispatch]);

  return (
    <div className="item-models-container">

      <style>{tableRowStyles}</style>
      
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Stocks</span>
              <span>Aged Stocks</span>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="box-container">
        <div className="table-wrapper">
          <TableComponent
            columns={columns}
            data={agedStocksData}
            setFilter={handleFilterChange}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
            excludeFields={excludeFieldsForRacks}
            context="agedstock"
          />
          
          {isLoading && (
            <div className="loading-overlay">
              <div className="spinner"></div>
            </div>
          )}
   
        </div>
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalItems={totalItems}
        />
      </div>
      {isModalOpen && selectedRow && (
        <RowDetailsModal
          rowData={selectedRow}
          onClose={handleCloseModal}
        
          excludeFields={excludeFieldsForRacks}
          menuTitle="Aged Stock Details"
   
        />
      )}
    </div>
  );
};

export default StockAged;